import { useValidation } from "@design-lab/shared/hooks/useValidation";
import { designSubTabAtom } from "@design-lab/shared/lib/tab-navigation";
import type { Tab } from "@design-lab/shared/types/tab";
import { useAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";

type UseRecaptureOptions = {
  setTab: (tab: Tab) => void;
  isSaveProjectVisible: boolean;
  setIsSaveProjectVisible: (visible: boolean) => void;
};

export function useRecapture({
  setTab,
  isSaveProjectVisible,
  setIsSaveProjectVisible
}: UseRecaptureOptions) {
  const { isFrontArtworkEnhanced } = useValidation();
  const isPendingTabChange = useRef(false);
  const [activeSubTab, setActiveSubTab] = useAtom(designSubTabAtom);

  const navigateToTabPostRecaptured = useCallback(() => {
    const targetPlacement = isFrontArtworkEnhanced ? "front" : "back";
    setTab("design");
    if (activeSubTab !== targetPlacement) {
      setActiveSubTab(targetPlacement);
    }
  }, [activeSubTab, isFrontArtworkEnhanced, setActiveSubTab, setTab]);

  const onRecaptured = useCallback(() => {
    if (isSaveProjectVisible) {
      isPendingTabChange.current = true;
      setIsSaveProjectVisible(false);
    } else {
      navigateToTabPostRecaptured();
    }
  }, [isSaveProjectVisible, navigateToTabPostRecaptured, setIsSaveProjectVisible]);

  useEffect(() => {
    if (!isSaveProjectVisible && isPendingTabChange.current) {
      isPendingTabChange.current = false;
      navigateToTabPostRecaptured();
    }
  }, [isSaveProjectVisible, navigateToTabPostRecaptured]);

  return { onRecaptured };
}
